/**
 * Chế độ «Xem từng người» của thẻ «Tình hình kinh doanh» (CEO 1a · 2a · 3b · 4a, 31/10/2026):
 * mọi người trong phạm vi đang xem, gom theo tiểu đội, sắp theo cột chọn, xếp hạng trong
 * tiểu đội (sales so với sales, CSKH so với CSKH), % góp vào tiểu đội, so tháng trước.
 * Chỉ dùng số máy chủ đã trả — không thêm số mới (4a). Thuần dữ liệu.
 */
import type { SalesOrgView, SalesTeamOverview, SalesTeamOverviewMetrics, SalesTeamOverviewNode } from "./api";
import { deltaPercent } from "./overview";

export type PeopleSortKey = "realRevenue" | "expected" | "orders" | "upsell" | "collected";

export const PEOPLE_SORT_LABEL: Record<PeopleSortKey, string> = {
  realRevenue: "Thực tế",
  expected: "Dự kiến",
  orders: "Số đơn",
  upsell: "Bán thêm",
  collected: "Đã đưa",
};

export type PersonRow = {
  node: SalesTeamOverviewNode;
  kind: "sales" | "cskh";
  /** Hạng trong tiểu đội, cùng loại người; đồng số thì đồng hạng (1, 1, 3). */
  rank: number;
  /** Số người cùng loại trong tiểu đội — hạng chỉ có nghĩa khi từ 2 người. */
  of: number;
  top: boolean;
  bottom: boolean;
  /** % thực tế của người trên thực tế tiểu đội; null khi tiểu đội chưa có số. */
  share: number | null;
  /** % chênh cột đang sắp so với tháng trước; null khi không có tháng trước để so. */
  delta: number | null;
};

export type SquadGroup = { squad: SalesTeamOverviewNode; people: PersonRow[] };

function value(m: SalesTeamOverviewMetrics, key: PeopleSortKey): number {
  return m[key];
}

/** Có nằm dưới `rootId` không (chính nó cũng tính). */
function under(byId: Map<string, SalesTeamOverviewNode>, id: string, rootId: string): boolean {
  const seen = new Set<string>();
  let cur = byId.get(id);
  while (cur && !seen.has(cur.id)) {
    if (cur.id === rootId) return true;
    seen.add(cur.id);
    cur = cur.parentId ? byId.get(cur.parentId) : undefined;
  }
  return false;
}

/** Loại người (sales / CSKH) theo cơ cấu HRM của đúng tiểu đội; không tra được thì coi là sales. */
function personKind(org: SalesOrgView | undefined, staffId: string, squadId: string): "sales" | "cskh" {
  const ms = org?.members.filter((m) => m.staffId === staffId) ?? [];
  const m = ms.find((x) => x.squadId === squadId) ?? ms[0];
  return m?.kind === "cskh" ? "cskh" : "sales";
}

/**
 * Nhóm theo tiểu đội dưới nút `focusId` (rỗng = mọi tiểu đội người xem được), tiểu đội và
 * người đều sắp giảm dần theo `key`.
 */
export function peopleBySquad(
  o: SalesTeamOverview,
  focusId: string | null,
  key: PeopleSortKey,
  org?: SalesOrgView,
): SquadGroup[] {
  const byId = new Map(o.nodes.map((n) => [n.id, n]));
  const squads = o.nodes.filter((n) => n.kind === "squad" && (!focusId || under(byId, n.id, focusId)));
  const groups = squads.map((squad) => {
    const members = o.nodes.filter((n) => n.kind === "person" && n.parentId === squad.id);
    const rows: PersonRow[] = members.map((node) => ({
      node,
      kind: personKind(org, node.id, squad.id),
      rank: 0,
      of: 0,
      top: false,
      bottom: false,
      share: squad.metrics.realRevenue > 0 ? Math.round((node.metrics.realRevenue / squad.metrics.realRevenue) * 100) : null,
      delta: deltaPercent(value(node.metrics, key), node.prev ? value(node.prev, key) : null),
    }));
    for (const kind of ["sales", "cskh"] as const) {
      const same = rows.filter((r) => r.kind === kind);
      const vals = same.map((r) => value(r.node.metrics, key));
      const hi = Math.max(...vals);
      const lo = Math.min(...vals);
      for (const r of same) {
        const v = value(r.node.metrics, key);
        r.rank = 1 + vals.filter((x) => x > v).length;
        r.of = same.length;
        // Tô người cao nhất / thấp nhất chỉ khi có từ 2 người và số khác nhau (3b).
        r.top = same.length >= 2 && hi > lo && v === hi;
        r.bottom = same.length >= 2 && hi > lo && v === lo;
      }
    }
    rows.sort(
      (a, b) =>
        (a.kind === b.kind ? 0 : a.kind === "sales" ? -1 : 1) ||
        value(b.node.metrics, key) - value(a.node.metrics, key) ||
        a.node.name.localeCompare(b.node.name, "vi"),
    );
    return { squad, people: rows };
  });
  return groups
    .filter((g) => g.people.length > 0)
    .sort((a, b) => value(b.squad.metrics, key) - value(a.squad.metrics, key) || a.squad.name.localeCompare(b.squad.name, "vi"));
}
